import {
  assertDatabaseColumnShapeWithinLimits,
  assertDatabaseWriteShapeWithinLimits,
  DATABASE_COLUMN_HARD_LIMIT,
  DATABASE_LARGE_DATA_POLICY,
  ROOT_DOC_ROW_MATERIALIZATION_HARD_LIMIT,
  ROW_SHARDED_DATABASE_GATE_MESSAGE,
} from '@streamlain/database-identity';

import type {
  DataSourceBulkRowsRequest,
  DataSourceCellsWindowRequest,
  DataSourceWindowRequest,
} from './base.js';

export {
  assertDatabaseColumnShapeWithinLimits,
  assertDatabaseWriteShapeWithinLimits,
  DATABASE_COLUMN_HARD_LIMIT,
  DATABASE_LARGE_DATA_POLICY,
  ROOT_DOC_ROW_MATERIALIZATION_HARD_LIMIT,
  ROW_SHARDED_DATABASE_GATE_MESSAGE,
};

export type NormalizedDataSourceWindow = {
  offset: number;
  limit: number;
};

export type NormalizedDataSourcePropertyWindow = {
  propertyOffset: number;
  propertyLimit: number;
  propertyIds: string[];
};

export const DATA_SOURCE_UI_WINDOW_MAX_LIMIT =
  DATABASE_LARGE_DATA_POLICY.ui.visibleRowWindowLimit;

export const DATA_SOURCE_BULK_ROW_MAX_LIMIT =
  DATABASE_LARGE_DATA_POLICY.jobs.bulkRowLimit;

export const DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT =
  DATABASE_LARGE_DATA_POLICY.ui.visibleColumnWindowLimit;

export const DATA_SOURCE_BULK_COLUMN_MAX_LIMIT =
  DATABASE_LARGE_DATA_POLICY.jobs.bulkColumnLimit;

export const DATA_SOURCE_KANBAN_GROUP_RENDER_LIMIT =
  DATABASE_LARGE_DATA_POLICY.render.kanbanGroupLimit;

export const DATA_SOURCE_KANBAN_ROW_GROUP_RENDER_LIMIT =
  DATABASE_LARGE_DATA_POLICY.render.kanbanRowsPerGroupLimit;

export const DATA_SOURCE_KANBAN_GROUP_CARD_RENDER_LIMIT =
  DATABASE_LARGE_DATA_POLICY.render.kanbanCardsPerGroupLimit;

export function clampDataSourceWindowLimit(
  totalCount: number,
  maxLimit: number = DATA_SOURCE_UI_WINDOW_MAX_LIMIT
): number {
  if (!Number.isInteger(totalCount) || totalCount < 0) {
    throw new RangeError(`Window total count must be a non-negative integer`);
  }
  if (!Number.isInteger(maxLimit) || maxLimit <= 0) {
    throw new RangeError(`Window max limit must be a positive integer`);
  }
  return Math.min(Math.max(totalCount, 1), maxLimit);
}

export function clampDataSourceWindowOffset(
  totalCount: number,
  offset: number,
  limit: number
): number {
  if (!Number.isInteger(totalCount) || totalCount < 0) {
    throw new RangeError(`Window total count must be a non-negative integer`);
  }
  if (!Number.isInteger(offset) || offset < 0) {
    throw new RangeError(`Window offset must be a non-negative integer`);
  }
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new RangeError(`Window limit must be a positive integer`);
  }
  return Math.min(offset, Math.max(0, totalCount - limit));
}

export function normalizeDataSourceWindow(
  request: DataSourceWindowRequest,
  maxLimit: number = DATA_SOURCE_UI_WINDOW_MAX_LIMIT
): NormalizedDataSourceWindow {
  const offset = request.offset ?? 0;
  const limit = request.limit;
  if (!Number.isInteger(offset) || offset < 0) {
    throw new RangeError(`Window offset must be a non-negative integer`);
  }
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new RangeError(`Window limit must be a positive integer`);
  }
  if (!Number.isInteger(maxLimit) || maxLimit <= 0) {
    throw new RangeError(`Window max limit must be a positive integer`);
  }
  if (limit > maxLimit) {
    throw new RangeError(
      `Window limit must be <= ${maxLimit}; use an explicit bulk API for larger reads`
    );
  }
  return { offset, limit };
}

export function normalizeDataSourcePropertyWindow(
  request: DataSourceCellsWindowRequest,
  propertyIds: readonly string[],
  maxLimit: number = DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT
): NormalizedDataSourcePropertyWindow {
  const propertyOffset = request.propertyOffset ?? 0;
  const propertyLimit = request.propertyLimit ?? propertyIds.length;
  if (!Number.isInteger(propertyOffset) || propertyOffset < 0) {
    throw new RangeError(
      `Property window offset must be a non-negative integer`
    );
  }
  if (propertyIds.length === 0 && propertyLimit === 0) {
    return {
      propertyOffset,
      propertyLimit,
      propertyIds: [],
    };
  }
  if (!Number.isInteger(propertyLimit) || propertyLimit <= 0) {
    throw new RangeError(`Property window limit must be a positive integer`);
  }
  if (propertyLimit > maxLimit) {
    throw new RangeError(`Property window limit must be <= ${maxLimit}`);
  }
  return {
    propertyOffset,
    propertyLimit,
    propertyIds: propertyIds.slice(
      propertyOffset,
      propertyOffset + propertyLimit
    ),
  };
}

export function assertDataSourceBulkJob(
  request: DataSourceBulkRowsRequest
): void {
  if (typeof request.jobId !== 'string' || request.jobId.trim() === '') {
    throw new TypeError(`Bulk database reads require a non-empty jobId`);
  }
  if (typeof request.purpose !== 'string' || request.purpose.trim() === '') {
    throw new TypeError(`Bulk database reads require a non-empty purpose`);
  }
  if (request.limit > DATA_SOURCE_BULK_ROW_MAX_LIMIT) {
    throw new RangeError(
      `Bulk database reads are limited to ${DATA_SOURCE_BULK_ROW_MAX_LIMIT} rows per request`
    );
  }
}
